import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  cancellations,
  claims,
  endorsementCatalog,
  endorsements,
  renewals,
  type CancellationRequest,
  type Claim,
  type ClaimStatus,
  type EndorsementDetail,
  type RenewalItem,
  type RenewalStatus,
  type RequestStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatMoney,
  moneyInputError,
  toMoney,
} from '@/shared/lib/format';
import { AcaoComIcone, LinkComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { KpiCard } from '@/shared/ui/KpiCard';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import {
  TabelaAjustavel,
  type EncaixeDaTabela,
  type LargurasDaTabela,
} from '@/shared/ui/TabelaAjustavel';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../documents/DocumentsPanel';

import { CancellationEndorsementsSection } from './CancellationEndorsementsSection';
import { caminhoDoAssistente, codigoDeEndosso } from './endosso';
import { EndorsementSettingsPanel } from './EndorsementSettingsPanel';

/**
 * Filas do pos-emissao.
 *
 * As quatro telas decidem sobre o que ja foi pedido. A solicitacao em si nasce na
 * apolice, onde a garantia nao e ambigua, e vive em `PolicyRequests`.
 */

const limite = 25;

/**
 * A lista de endossos fica fluida, sem rolar para o lado: com todas as colunas a partir
 * de 1280 px de area, e reunida a partir de 900 (1366 e 1440 com o menu aberto).
 */
const largurasDosEndossos: LargurasDaTabela = { ampla: 1280, reunida: 900 };

const situacoesDeSolicitacao = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'solicitado', label: 'Solicitado' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'recusado', label: 'Recusado' },
];

/**
 * Ligacao para a apolice de origem, presente em toda linha das filas. Leva ao detalhe
 * da apolice (P-APO-1): antes ia para a lista, sem destacar a apolice (defeito 6 de
 * "Apolice" do inventario 47, anexo C).
 */
/** O olho que abre a apolice na carteira, quando a linha nao pede decisao. */
function ApoliceLink({ policyId }: { policyId: string }) {
  return (
    <LinkComIcone acao="abrir" rotulo="Ver na carteira" to={`/apolices/${policyId}`} />
  );
}

function Detalhe({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-0.5 truncate text-sm text-ink-900">{valor || '--'}</p>
    </div>
  );
}

/**
 * Dialogo de decisao das solicitacoes que so tem duas saidas.
 *
 * A recusa exige justificativa. Aprovar e recusar compartilham o dialogo porque
 * compartilham a decisao: separa-los faria a mesma regra ser escrita duas vezes.
 */
function DecisionDialog({
  open,
  onClose,
  titulo,
  descricao,
  pendente,
  onDecidir,
}: {
  open: boolean;
  onClose: () => void;
  titulo: string;
  descricao: string;
  pendente: boolean;
  onDecidir: (status: RequestStatus, note: string) => void;
}) {
  const [nota, setNota] = useState('');

  function fechar() {
    setNota('');
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={titulo}
      description={descricao}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={pendente}
            disabled={nota.trim() === ''}
            onClick={() => onDecidir('recusado', nota)}
          >
            Recusar
          </Button>
          <Button loading={pendente} onClick={() => onDecidir('aprovado', nota)}>
            Aprovar
          </Button>
        </>
      }
    >
      <Textarea
        label="Justificativa da decisao"
        value={nota}
        hint="Obrigatoria na recusa. Fica registrada na trilha de auditoria."
        onChange={(event) => setNota(event.target.value)}
      />
    </Modal>
  );
}

// --- Endossos --------------------------------------------------------------

const situacoesDoEndosso = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'solicitado', label: 'Solicitado' },
  { value: 'em_analise', label: 'Em analise na esteira' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'emitido', label: 'Emitido' },
  { value: 'recusado', label: 'Recusado' },
  { value: 'cancelado', label: 'Cancelado' },
];

/**
 * Fila de endossos.
 *
 * A aprovacao aplica a alteracao na apolice no mesmo instante em que decide. Por
 * isso a tela mostra o que sera aplicado, e nao apenas quem pediu o que. Cada linha
 * traz o numero e o tomador da apolice, o tipo e o subtipo do catalogo e, no fluxo da
 * esteira, a operacao do tipo endosso (P-END-1; a fila sem o numero e o tomador era
 * defeito do 47, secao 3.6).
 */
export function EndorsementsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [tipo, setTipo] = useState('');
  const [offset, setOffset] = useState(0);
  const [decidindo, setDecidindo] = useState<EndorsementDetail | null>(null);
  const [documentosDe, setDocumentosDe] = useState<EndorsementDetail | null>(null);
  const [configurando, setConfigurando] = useState(false);

  const podeDecidir = can('endossos.endosso.approve');
  const podeConfigurar = can('endossos.configuracao.update');
  // Painel comum de documentos (P-DOC), para quem le o repositorio.
  const podeVerDocumentos = can('documentos.documento.view');

  const consulta = useQuery({
    queryKey: ['endorsements', { status, tipo, offset }],
    queryFn: ({ signal }) =>
      endorsements.list(
        { status, endorsement_type_id: tipo || undefined, limit: limite, offset },
        signal,
      ),
  });
  const tipos = useQuery({
    queryKey: ['endorsement-types', 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.types(false, signal),
    retry: false,
  });

  const decidir = useMutation({
    mutationFn: ({
      id,
      status: decisao,
      note,
    }: {
      id: string;
      status: RequestStatus;
      note: string;
    }) => endorsements.decide(id, { status: decisao, note }),
    onSuccess: (endosso) => {
      toast.success(
        endosso.status === 'aprovado'
          ? 'Endosso aprovado e aplicado na apolice.'
          : 'Endosso recusado.',
      );
      setDecidindo(null);
      void queryClient.invalidateQueries({ queryKey: ['endorsements'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // As larguras valem na tabela fluida, que nao rola para o lado nas telas comuns
  // (04/11/2026); na reunida, quem pediu vai para a segunda linha da alteracao e a
  // operacao para a da situacao.
  const colunas: Column<EndorsementDetail>[] = [
    {
      key: 'numero',
      header: 'Endosso',
      width: '10rem',
      render: (endosso) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{endosso.number}</p>
          <p className="truncate text-2xs text-ink-500">
            {endosso.endorsement_reason?.name ?? endosso.reason}
          </p>
        </div>
      ),
    },
    {
      key: 'apolice',
      header: 'Apolice',
      hideBelow: 'md',
      render: (endosso) =>
        endosso.policy ? (
          <div className="min-w-0">
            <p className="truncate text-ink-900">
              {endosso.policy.external_number || String(endosso.policy.number)}
            </p>
            <p className="truncate text-2xs text-ink-500">
              {endosso.policy.policyholder_name}
            </p>
          </div>
        ) : (
          '--'
        ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '13%',
      render: (endosso) =>
        endosso.kind ? (
          <StatusBadge kind="endosso" value={endosso.kind} />
        ) : (
          <div className="min-w-0">
            <StatusBadge
              kind="tipoEndosso"
              value={endosso.endorsement_type?.code ?? ''}
            />
            <p className="truncate text-2xs text-ink-500">
              {endosso.endorsement_subtype?.name}
            </p>
          </div>
        ),
    },
    {
      key: 'alteracao',
      header: 'Alteracao',
      align: 'right',
      hideBelow: 'md',
      width: '9.5rem',
      render: (endosso) => <AlteracaoCelula endorsement={endosso} />,
    },
    {
      key: 'operacao',
      header: 'Operacao',
      hideBelow: 'lg',
      width: '11rem',
      render: (endosso) =>
        endosso.operation ? (
          <Link
            to={`/operacoes/${endosso.operation.id}`}
            className="block truncate text-brand-600 underline-offset-2 hover:underline"
          >
            {endosso.operation.number} -{' '}
            {statusLabel('operacao', endosso.operation.stage)}
          </Link>
        ) : (
          <span className="text-ink-500">--</span>
        ),
    },
    {
      key: 'solicitante',
      header: 'Solicitado por',
      hideBelow: 'lg',
      width: '11%',
      render: (endosso) => (
        <span title={endosso.requested_label} className="block truncate">
          {endosso.requested_label}
        </span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      width: '9.5rem',
      render: (endosso) => (
        <StatusBadge kind="situacaoEndosso" value={endosso.status} />
      ),
    },
    ...(podeVerDocumentos
      ? [
          {
            key: 'documentos',
            header: 'Documentos',
            hideBelow: 'md' as const,
            width: '8.5rem',
            render: (endosso: EndorsementDetail) => (
              <AcaoComIcone
                acao="anexar"
                rotulo="Documentos"
                onClick={() => setDocumentosDe(endosso)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'acao',
      header: 'Decisao',
      align: 'right',
      width: '10rem',
      render: (endosso) => {
        const codigo = codigoDeEndosso(endosso.endorsement_type?.code);
        const doAssistente =
          !endosso.kind && codigo !== null && endosso.endorsement_subtype_id;
        return (
          <div className="flex items-center justify-end gap-1">
            {podeDecidir && endosso.status === 'solicitado' ? (
              <AcaoComIcone
                acao="decidir"
                rotulo="Decidir"
                onClick={() => setDecidindo(endosso)}
              />
            ) : null}
            {doAssistente ? (
              <LinkComIcone
                acao="abrir"
                rotulo="Abrir o endosso"
                to={caminhoDoAssistente(
                  endosso.policy_id,
                  codigo,
                  endosso.endorsement_subtype_id ?? '',
                  endosso.id,
                )}
              />
            ) : null}
            {!(podeDecidir && endosso.status === 'solicitado') && !doAssistente ? (
              <ApoliceLink policyId={endosso.policy_id} />
            ) : null}
          </div>
        );
      },
    },
  ];

  const porChave = new Map(colunas.map((coluna) => [coluna.key, coluna]));
  const colunasDoEncaixe = (encaixe: EncaixeDaTabela): Column<EndorsementDetail>[] => {
    if (encaixe !== 'reunida') return colunas;
    const operacao = porChave.get('operacao');
    const solicitante = porChave.get('solicitante');
    return colunas.flatMap((coluna): Column<EndorsementDetail>[] => {
      if (coluna.key === 'operacao' || coluna.key === 'solicitante') return [];
      if (coluna.key === 'alteracao' && solicitante) {
        return [
          {
            ...coluna,
            detail: 'Solicitado por',
            render: (endosso) => (
              <div className="min-w-0">
                <span className="block truncate">{coluna.render(endosso)}</span>
                <span className="block truncate text-2xs text-ink-500">
                  {solicitante.render(endosso)}
                </span>
              </div>
            ),
          },
        ];
      }
      if (coluna.key === 'situacao' && operacao) {
        return [
          {
            ...coluna,
            width: '11rem',
            detail: 'Operacao',
            render: (endosso) => (
              <div className="min-w-0">
                {coluna.render(endosso)}
                <span className="block truncate text-2xs">
                  {operacao.render(endosso)}
                </span>
              </div>
            ),
          },
        ];
      }
      return [coluna];
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Endossos"
        description="Alteracoes de apolice emitida, da solicitacao a aplicacao."
        actions={
          podeConfigurar ? (
            <Button variant="secondary" onClick={() => setConfigurando(true)}>
              Configuracao
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os endossos">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Situacao"
            className="h-9"
            options={situacoesDoEndosso}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
          {tipos.isSuccess ? (
            <Select
              label="Tipo de endosso"
              className="h-9"
              placeholder="Todos os tipos"
              options={tipos.data.map((item) => ({ value: item.id, label: item.name }))}
              value={tipo}
              onChange={(event) => {
                setTipo(event.target.value);
                setOffset(0);
              }}
            />
          ) : null}
        </div>

        <TabelaAjustavel
          colunas={colunasDoEncaixe}
          larguras={largurasDosEndossos}
          items={consulta.data?.items ?? []}
          keyOf={(endosso) => endosso.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="copy"
              title={
                status ? 'Nenhum endosso nessa situacao' : 'Nenhum endosso solicitado'
              }
              description="O endosso nasce na apolice: abra a garantia na carteira e solicite a alteracao por la."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {/* A chave remonta o dialogo a cada endosso: a justificativa de uma decisao nao
          fica no campo para a proxima (47, secao 3.6). */}
      <DecisionDialog
        key={decidindo?.id ?? 'nenhum'}
        open={decidindo !== null}
        onClose={() => setDecidindo(null)}
        titulo="Decidir endosso"
        descricao="A aprovacao aplica a alteracao na apolice no mesmo instante."
        pendente={decidir.isPending}
        onDecidir={(decisao, note) =>
          decidindo && decidir.mutate({ id: decidindo.id, status: decisao, note })
        }
      />

      <Modal
        open={documentosDe !== null}
        onClose={() => setDocumentosDe(null)}
        title={documentosDe ? `Documentos do endosso ${documentosDe.number}` : ''}
        description={documentosDe?.reason}
        size="lg"
        footer={<Button onClick={() => setDocumentosDe(null)}>Fechar</Button>}
      >
        {documentosDe ? (
          <DocumentsPanel
            ownerType="endosso"
            ownerId={documentosDe.id}
            title="Documentos do endosso"
            group="Endosso"
            allowDelete
          />
        ) : null}
      </Modal>

      {podeConfigurar ? (
        <EndorsementSettingsPanel
          open={configurando}
          onClose={() => setConfigurando(false)}
        />
      ) : null}
    </div>
  );
}

function AlteracaoCelula({ endorsement }: { endorsement: EndorsementDetail }) {
  if (endorsement.amount_delta) {
    const sinal = endorsement.amount_delta.amount > 0 ? '+' : '';
    return (
      <span
        className={
          endorsement.amount_delta.amount > 0 ? 'text-ink-900' : 'text-warning-700'
        }
      >
        {sinal}
        {formatMoney(endorsement.amount_delta)}
      </span>
    );
  }
  if (endorsement.new_coverage_end) {
    return <span>ate {formatCivilDate(endorsement.new_coverage_end)}</span>;
  }
  if (
    endorsement.endorsement_type?.code === 'neutro' ||
    !endorsement.calculation_pending
  ) {
    return <span className="text-ink-500">Sem efeito financeiro</span>;
  }
  return <span className="text-warning-700">Calculo pendente</span>;
}

// --- Cancelamentos ---------------------------------------------------------

/**
 * Fila de cancelamentos.
 *
 * A apolice continua vigente enquanto a solicitacao esta aberta. A aprovacao e o
 * unico ponto em que a garantia deixa de valer.
 *
 * P-END-2 (decisao 3 da 11.7): o cancelamento tambem e um tipo de endosso, listado
 * abaixo. Ate a regra de restituicao, a solicitacao aprovada cancela sem restituir e
 * sem mexer na comissao, e a tela diz isso.
 */
export function CancellationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [decidindo, setDecidindo] = useState<CancellationRequest | null>(null);

  const podeDecidir = can('cancelamentos.cancelamento.approve');
  const podeVerEndossos = can('endossos.endosso.view');

  const consulta = useQuery({
    queryKey: ['cancellations', { status, offset }],
    queryFn: ({ signal }) =>
      cancellations.list({ status, limit: limite, offset }, signal),
  });

  const decidir = useMutation({
    mutationFn: ({
      id,
      status: decisao,
      note,
    }: {
      id: string;
      status: RequestStatus;
      note: string;
    }) => cancellations.decide(id, { status: decisao, note }),
    onSuccess: (pedido) => {
      toast.success(
        pedido.status === 'aprovado'
          ? 'Apolice cancelada.'
          : 'Cancelamento recusado. A apolice continua vigente.',
      );
      setDecidindo(null);
      void queryClient.invalidateQueries({ queryKey: ['cancellations'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const colunas: Column<CancellationRequest>[] = [
    {
      key: 'motivo',
      header: 'Motivo',
      render: (pedido) => (
        <p className="min-w-0 truncate text-ink-900">{pedido.reason}</p>
      ),
    },
    {
      key: 'solicitante',
      header: 'Solicitado por',
      hideBelow: 'md',
      render: (pedido) => pedido.requested_label,
    },
    {
      key: 'efeito',
      header: 'Efeito',
      align: 'right',
      hideBelow: 'lg',
      render: (pedido) =>
        pedido.effective_date ? formatCivilDate(pedido.effective_date) : '--',
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (pedido) => <StatusBadge kind="solicitacao" value={pedido.status} />,
    },
    {
      key: 'acao',
      header: 'Decisao',
      align: 'right',
      render: (pedido) =>
        podeDecidir && pedido.status === 'solicitado' ? (
          <AcaoComIcone
            acao="decidir"
            rotulo="Decidir"
            onClick={() => setDecidindo(pedido)}
          />
        ) : (
          <ApoliceLink policyId={pedido.policy_id} />
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Cancelamentos"
        description="Solicitacoes de encerramento de garantia e a decisao de cada uma."
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os cancelamentos">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Alert tone="info" title="Comissao e restituicao no cancelamento">
        A aprovacao do cancelamento nao altera a comissao e nao restitui premio: o
        estorno da comissao e a restituicao sao regra pendente (PRC-006, E13, END-005,
        PRC-010). O endosso de cancelamento ja guarda a data e a conta para a
        restituicao.
      </Alert>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Situacao"
            className="h-9"
            options={situacoesDeSolicitacao}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(pedido) => pedido.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="close"
              title={
                status
                  ? 'Nenhuma solicitacao nessa situacao'
                  : 'Nenhum cancelamento solicitado'
              }
              description="O cancelamento nasce na apolice: abra a garantia na carteira e solicite o encerramento por la."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {podeVerEndossos ? <CancellationEndorsementsSection /> : null}

      <DecisionDialog
        key={decidindo?.id ?? 'nenhum'}
        open={decidindo !== null}
        onClose={() => setDecidindo(null)}
        titulo="Decidir cancelamento"
        descricao="A aprovacao encerra a garantia a partir da data da decisao."
        pendente={decidir.isPending}
        onDecidir={(decisao, note) =>
          decidindo && decidir.mutate({ id: decidindo.id, status: decisao, note })
        }
      />
    </div>
  );
}

// --- Renovacoes ------------------------------------------------------------

/**
 * "Prazo da modalidade" e a janela padrao: o prazo para criacao da cotacao da versao em
 * vigor dos parametros da renovacao de cada modalidade, e 90 dias para a modalidade sem
 * parametro em vigor (P-PAR-3). As outras opcoes valem para todas as modalidades.
 */
const PRAZO_DA_MODALIDADE = 'modalidade';

const horizontes = [
  { value: PRAZO_DA_MODALIDADE, label: 'Prazo da modalidade' },
  { value: '30', label: 'Vence em 30 dias' },
  { value: '60', label: 'Vence em 60 dias' },
  { value: '90', label: 'Vence em 90 dias' },
  { value: '180', label: 'Vence em 180 dias' },
];

const situacoesDeRenovacao = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_cotacao', label: 'Em cotacao' },
  { value: 'renovada', label: 'Renovada' },
  { value: 'nao_renovada', label: 'Nao renovada' },
];

/** De onde veio a janela que pos a apolice na fila. */
function origemDaJanela(item: RenewalItem): string {
  switch (item.window_source) {
    case 'modalidade':
      return `Janela da modalidade: ${item.window_days} d`;
    case 'padrao':
      return `Janela padrao: ${item.window_days} d`;
    default:
      return `Janela informada: ${item.window_days} d`;
  }
}

/**
 * Carteira a vencer.
 *
 * A fila e uma consulta sobre a carteira. O acompanhamento e o que a consulta nao
 * sabe: se alguem ja assumiu aquela apolice e no que deu. Ausencia de
 * acompanhamento significa que ninguem assumiu, e nao que a apolice dispense
 * renovacao.
 */
export function RenewalsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [horizonte, setHorizonte] = useState(PRAZO_DA_MODALIDADE);
  const [offset, setOffset] = useState(0);
  const [acompanhando, setAcompanhando] = useState<RenewalItem | null>(null);
  const [situacao, setSituacao] = useState<RenewalStatus>('em_cotacao');
  const [operacao, setOperacao] = useState('');
  const [notas, setNotas] = useState('');

  const podeAcompanhar = can('renovacoes.renovacao.update');

  const consulta = useQuery({
    queryKey: ['renewals', { horizonte, offset }],
    queryFn: ({ signal }) =>
      renewals.list(
        horizonte === PRAZO_DA_MODALIDADE
          ? { limit: limite, offset }
          : { horizon_days: Number(horizonte), limit: limite, offset },
        signal,
      ),
  });

  const acompanhar = useMutation({
    mutationFn: () => {
      if (!acompanhando) throw new Error('Apolice nao informada.');
      return renewals.track(acompanhando.policy.id, {
        status: situacao,
        new_operation_id: situacao === 'renovada' ? operacao : null,
        notes: notas,
        take_ownership: true,
      });
    },
    onSuccess: () => {
      toast.success('Acompanhamento atualizado.');
      setAcompanhando(null);
      void queryClient.invalidateQueries({ queryKey: ['renewals'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function abrir(item: RenewalItem) {
    setAcompanhando(item);
    setSituacao(item.renewal?.status ?? 'em_cotacao');
    setOperacao(item.renewal?.new_operation_id ?? '');
    setNotas(item.renewal?.notes ?? '');
  }

  const itens = consulta.data?.items ?? [];
  const semDono = itens.filter((item) => item.renewal === null).length;
  const criticas = itens.filter((item) => item.days_to_expiry <= 30).length;

  const colunas: Column<RenewalItem>[] = [
    {
      key: 'apolice',
      header: 'Apolice',
      render: (item) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">
            {item.policy.external_number || item.policy.number}
          </p>
          <p className="truncate text-2xs text-ink-500">
            {item.policy.policyholder.label}
          </p>
        </div>
      ),
    },
    {
      key: 'valor',
      header: 'Valor garantido',
      align: 'right',
      hideBelow: 'md',
      render: (item) => formatMoney(item.policy.insured_amount),
    },
    {
      key: 'vencimento',
      header: 'Vence em',
      align: 'right',
      render: (item) => (
        <div className="flex flex-col items-end">
          <span
            className={
              item.days_to_expiry <= 30
                ? 'font-medium text-danger-700'
                : item.days_to_expiry <= 60
                  ? 'font-medium text-warning-700'
                  : 'text-ink-700'
            }
          >
            {item.days_to_expiry} d
          </span>
          <span className="text-2xs text-ink-500">
            {formatCivilDate(item.policy.coverage_end)}
          </span>
          <span className="text-2xs text-ink-500">{origemDaJanela(item)}</span>
        </div>
      ),
    },
    {
      key: 'acompanhamento',
      header: 'Acompanhamento',
      render: (item) =>
        item.renewal ? (
          <div className="flex flex-col gap-1">
            <StatusBadge kind="renovacao" value={item.renewal.status} />
            <span className="text-2xs text-ink-500">{item.renewal.owner_label}</span>
          </div>
        ) : (
          <span className="text-2xs text-ink-500">Sem responsavel</span>
        ),
    },
    {
      key: 'acao',
      header: 'Acao',
      align: 'right',
      render: (item) =>
        podeAcompanhar ? (
          <AcaoComIcone
            icone="clock"
            tom="cyan"
            rotulo="Acompanhar"
            onClick={() => abrir(item)}
          />
        ) : (
          <ApoliceLink policyId={item.policy.id} />
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Renovacoes"
        description="Carteira a vencer e o acompanhamento de cada garantia."
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a fila">
          {consulta.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          label="Na janela"
          value={String(consulta.data?.total ?? 0)}
          icon="refresh"
          tone="cyan"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Vencem em ate 30 dias"
          value={String(criticas)}
          icon="clock"
          tone="warning"
          highlight={criticas > 0}
          loading={consulta.isPending}
        />
        <KpiCard
          label="Sem responsavel"
          value={String(semDono)}
          hint="Ninguem assumiu o acompanhamento."
          icon="users"
          tone="warning"
          highlight={semDono > 0}
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Janela"
            className="h-9"
            options={horizontes}
            value={horizonte}
            onChange={(event) => {
              setHorizonte(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={itens}
          keyOf={(item) => item.policy.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="refresh"
              title="Nenhuma apolice a vencer na janela"
              description="Amplie a janela para enxergar vencimentos mais distantes."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={acompanhando !== null}
        onClose={() => setAcompanhando(null)}
        title="Acompanhar renovacao"
        description="A garantia renovada percorre a mesma esteira de uma garantia nova."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAcompanhando(null)}>
              Voltar
            </Button>
            <Button
              loading={acompanhar.isPending}
              disabled={situacao === 'renovada' && operacao.trim() === ''}
              onClick={() => acompanhar.mutate()}
            >
              Salvar acompanhamento
            </Button>
          </>
        }
      >
        {acompanhando ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Detalhe
                rotulo="Apolice"
                valor={
                  acompanhando.policy.external_number ||
                  String(acompanhando.policy.number)
                }
              />
              <Detalhe
                rotulo="Vencimento"
                valor={formatCivilDate(acompanhando.policy.coverage_end)}
              />
            </div>

            <Select
              label="Situacao"
              options={situacoesDeRenovacao}
              value={situacao}
              onChange={(event) => setSituacao(event.target.value as RenewalStatus)}
            />

            {situacao === 'renovada' ? (
              <Field
                label="Identificador da operacao nova"
                required
                value={operacao}
                hint="Sem a operacao nova, a renovacao ficaria afirmada sem prova."
                onChange={(event) => setOperacao(event.target.value)}
              />
            ) : null}

            <Textarea
              label="Anotacoes"
              value={notas}
              onChange={(event) => setNotas(event.target.value)}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// --- Sinistros -------------------------------------------------------------

const situacoesDeSinistro = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_analise', label: 'Em analise' },
  { value: 'pago', label: 'Pago' },
  { value: 'negado', label: 'Negado' },
];

/**
 * Sinistros.
 *
 * O sinistro anda de aberto para em analise, e dai para pago ou negado. A tela
 * oferece apenas as transicoes possiveis a partir da situacao atual: oferecer o
 * pagamento a um sinistro que ninguem analisou seria convidar ao erro.
 */
export function ClaimsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [decidindo, setDecidindo] = useState<Claim | null>(null);
  const [alvo, setAlvo] = useState<ClaimStatus>('em_analise');
  const [valorPago, setValorPago] = useState('');
  const [nota, setNota] = useState('');

  const podeDecidir = can('sinistros.sinistro.approve');

  const consulta = useQuery({
    queryKey: ['claims', { status, offset }],
    queryFn: ({ signal }) => claims.list({ status, limit: limite, offset }, signal),
  });

  const decidir = useMutation({
    mutationFn: () => {
      if (!decidindo) throw new Error('Sinistro nao informado.');
      return claims.decide(decidindo.id, {
        status: alvo,
        paid_amount: alvo === 'pago' ? toMoney(valorPago || '0') : null,
        note: nota,
      });
    },
    onSuccess: () => {
      toast.success('Sinistro atualizado.');
      setDecidindo(null);
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function abrir(claim: Claim) {
    setDecidindo(claim);
    setAlvo(claim.status === 'aberto' ? 'em_analise' : 'pago');
    setValorPago('');
    setNota('');
  }

  const contagem = consulta.data?.byStatus ?? {};

  const colunas: Column<Claim>[] = [
    {
      key: 'numero',
      header: 'Sinistro',
      render: (claim) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{claim.number}</p>
          <p className="truncate text-2xs text-ink-500">{claim.kind}</p>
        </div>
      ),
    },
    {
      key: 'reclamado',
      header: 'Reclamado',
      align: 'right',
      render: (claim) => formatMoney(claim.claimed_amount),
    },
    {
      key: 'pago',
      header: 'Pago',
      align: 'right',
      hideBelow: 'md',
      render: (claim) =>
        claim.paid_amount.amount > 0 ? (
          formatMoney(claim.paid_amount)
        ) : (
          <span className="text-ink-400">--</span>
        ),
    },
    {
      key: 'aviso',
      header: 'Aviso',
      align: 'right',
      hideBelow: 'lg',
      render: (claim) => formatCivilDate(claim.notice_date),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (claim) => <StatusBadge kind="sinistro" value={claim.status} />,
    },
    {
      key: 'acao',
      header: 'Decisao',
      align: 'right',
      render: (claim) =>
        podeDecidir && (claim.status === 'aberto' || claim.status === 'em_analise') ? (
          <AcaoComIcone acao="decidir" rotulo="Decidir" onClick={() => abrir(claim)} />
        ) : (
          <ApoliceLink policyId={claim.policy_id} />
        ),
    },
  ];

  // A partir de aberto, a unica saida util e a analise. A negativa direta existe
  // para o acionamento que nem chega a ser analisado.
  const alvosPossiveis =
    decidindo?.status === 'aberto'
      ? [
          { value: 'em_analise', label: 'Colocar em analise' },
          { value: 'negado', label: 'Negar' },
        ]
      : [
          { value: 'pago', label: 'Pagar' },
          { value: 'negado', label: 'Negar' },
        ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Sinistros"
        description="Acionamento da garantia, analise e decisao."
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os sinistros">
          {consulta.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-4">
        <KpiCard
          label="Abertos"
          value={String(contagem.aberto ?? 0)}
          icon="alert"
          tone="warning"
          highlight={(contagem.aberto ?? 0) > 0}
          loading={consulta.isPending}
        />
        <KpiCard
          label="Em analise"
          value={String(contagem.em_analise ?? 0)}
          icon="clock"
          tone="info"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Pagos"
          value={String(contagem.pago ?? 0)}
          icon="coins"
          tone="success"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Negados"
          value={String(contagem.negado ?? 0)}
          icon="close"
          tone="danger"
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Situacao"
            className="h-9"
            options={situacoesDeSinistro}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(claim) => claim.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="alert"
              title={
                status ? 'Nenhum sinistro nessa situacao' : 'Nenhum sinistro registrado'
              }
              description="O sinistro nasce na apolice acionada: abra a garantia na carteira e registre o acionamento por la."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={decidindo !== null}
        onClose={() => setDecidindo(null)}
        title="Decidir sinistro"
        description="Pago e negado sao terminais: um acionamento novo sobre a mesma apolice e outro sinistro."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDecidindo(null)}>
              Voltar
            </Button>
            <Button
              loading={decidir.isPending}
              disabled={
                (alvo === 'pago' && toMoney(valorPago || '0').amount <= 0) ||
                (alvo === 'negado' && nota.trim() === '')
              }
              onClick={() => decidir.mutate()}
            >
              Registrar decisao
            </Button>
          </>
        }
      >
        {decidindo ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Detalhe rotulo="Sinistro" valor={String(decidindo.number)} />
              <Detalhe
                rotulo="Valor reclamado"
                valor={formatMoney(decidindo.claimed_amount)}
              />
            </div>

            <p className="text-sm text-ink-700">{decidindo.description}</p>

            <Select
              label="Decisao"
              options={alvosPossiveis}
              value={alvo}
              onChange={(event) => setAlvo(event.target.value as ClaimStatus)}
            />

            {alvo === 'pago' ? (
              <MoneyField
                label="Valor pago (R$)"
                required
                value={valorPago}
                error={moneyInputError(valorPago)}
                hint="A garantia responde ate o limite emitido na apolice."
                onValueChange={setValorPago}
              />
            ) : null}

            <Textarea
              label="Justificativa da decisao"
              value={nota}
              hint="Obrigatoria na negativa."
              onChange={(event) => setNota(event.target.value)}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
